// Comprehensive connector list for all major cable types
// This file now uses compatibilityMatrix.js as the single source of truth
// id, partNumber, type, gender, brand, description, skuCode, verified

// Import the compatibility matrix as the single source of truth
import { compatibilityMatrix } from './compatibilityMatrix';

// Legacy connector list - now derived from compatibility matrix
export const connectorList = Object.values(compatibilityMatrix)
  .filter(connector => connector && connector.id)
  .map(connector => ({
    id: connector.id,
    partNumber: connector.partNumber || connector.id,
    type: connector.type || 'unknown',
    gender: connector.gender || 'unknown',
    brand: connector.brand || 'Generic',
    description: connector.description || connector.label || connector.id,
    skuCode: connector.skuCode || 'UNKNOWN',
    verified: connector.verified !== false, // Default to true unless explicitly false
    pins: connector.pins || 0,
    family: connector.family || 'Unknown',
    image: connector.image || '/images/placeholder.png',
    // Preserve pricing if available
    ...(connector.inlinePricing && { inlinePricing: connector.inlinePricing }),
    ...(connector.panelMountPricing && { panelMountPricing: connector.panelMountPricing })
  }));

// Power connectors - derived from compatibility matrix
export const powerConnectors = Object.values(compatibilityMatrix)
  .filter(connector => connector && connector.type === 'power')
  .map(connector => ({
    id: connector.id,
    type: connector.type,
    family: connector.family || 'Unknown',
    mount: connector.mount || connector.configuration || 'unknown',
    gender: connector.gender || 'unknown',
    side: connector.side || 'unknown',
    image: connector.image || '/images/placeholder.png',
    label: connector.label || connector.description || connector.id,
    skuCode: connector.skuCode || 'UNKNOWN',
    description: connector.description || connector.label || connector.id,
    verified: connector.verified !== false
  }));

// Audio connectors - derived from compatibility matrix
export const audioConnectors = Object.values(compatibilityMatrix)
  .filter(connector => connector && 
    (connector.type === 'audio' || 
     connector.type === 'XLR' || 
     connector.type === 'TRS' || 
     connector.type === 'TS' || 
     connector.type === 'RCA' ||
     connector.family === 'XLR' ||
     connector.family === 'TRS' ||
     connector.family === 'TS' ||
     connector.family === 'RCA'))
  .map(connector => ({
    id: connector.id,
    type: connector.type,
    family: connector.family || connector.type,
    mount: connector.mount || connector.configuration || 'inline',
    gender: connector.gender || 'unknown',
    image: connector.image || '/images/placeholder.png',
    label: connector.label || connector.description || connector.id,
    skuCode: connector.skuCode || 'UNKNOWN',
    description: connector.description || connector.label || connector.id,
    verified: connector.verified !== false,
    pins: connector.pins || 0
  }));

// DMX connectors - derived from compatibility matrix
export const dmxConnectors = Object.values(compatibilityMatrix)
  .filter(connector => connector && 
    (connector.type === 'dmx' || 
     connector.application === 'DMX' ||
     (connector.family === 'XLR' && connector.pins === 5)))
  .map(connector => ({
    id: connector.id,
    type: connector.type,
    family: connector.family || connector.type,
    mount: connector.mount || connector.configuration || 'inline',
    gender: connector.gender || 'unknown',
    image: connector.image || '/images/placeholder.png',
    label: connector.label || connector.description || connector.id,
    skuCode: connector.skuCode || 'UNKNOWN',
    description: connector.description || connector.label || connector.id,
    verified: connector.verified !== false,
    pins: connector.pins || 5
  }));

// Speaker connectors - derived from compatibility matrix
export const speakerConnectors = Object.values(compatibilityMatrix)
  .filter(connector => connector && 
    (connector.type === 'speaker' || 
     connector.type === 'Speakon' ||
     connector.family === 'Speakon'))
  .map(connector => ({
    id: connector.id,
    type: connector.type,
    family: connector.family || connector.type,
    mount: connector.mount || connector.configuration || 'inline',
    gender: connector.gender || 'unknown',
    image: connector.image || '/images/placeholder.png',
    label: connector.label || connector.description || connector.id,
    skuCode: connector.skuCode || 'UNKNOWN',
    description: connector.description || connector.label || connector.id,
    verified: connector.verified !== false,
    pins: connector.pins || 0
  }));

// Enhanced connector organization system
export const connectorPurposes = {
  microphone: {
    name: 'Microphone',
    description: 'Professional microphone cables for vocal and instrument recording',
    primaryConnectors: ['XLR', 'TRS'],
    commonUses: ['Live Sound', 'Studio Recording', 'Broadcast'],
    recommendedConnectors: ['NC3FXX', 'NC3MXX', 'XLR3F', 'XLR3M']
  },
  instrument: {
    name: 'Instrument',
    description: 'Instrument cables for guitars, keyboards, and line-level equipment',
    primaryConnectors: ['TRS', 'TS'],
    commonUses: ['Live Sound', 'Studio Recording', 'Installation'],
    recommendedConnectors: ['TRS14M', 'TRS14F', 'TS14M', 'TS14F']
  },
  speaker: {
    name: 'Speaker',
    description: 'Speaker cables for passive speakers and amplifiers',
    primaryConnectors: ['Speakon', 'TRS', 'TS'],
    commonUses: ['Live Sound', 'Installation', 'Touring'],
    recommendedConnectors: ['SPK4M', 'SPK4F', 'SPK2M', 'SPK2F']
  },
  power: {
    name: 'Power',
    description: 'Power cables for equipment and electrical distribution',
    primaryConnectors: ['PowerCon', 'IEC', 'NEMA'],
    commonUses: ['Live Sound', 'Installation', 'Touring'],
    recommendedConnectors: ['PCB', 'PCG', 'C13', 'C14']
  },
  dmx: {
    name: 'DMX/Lighting',
    description: 'DMX control cables for lighting and effects equipment',
    primaryConnectors: ['XLR'],
    commonUses: ['Live Sound', 'Installation', 'Broadcast'],
    recommendedConnectors: ['XLR3M-DMX', 'XLR3F-DMX', 'XLR5M-DMX', 'XLR5F-DMX']
  },
  video: {
    name: 'Video',
    description: 'Video cables for displays, cameras, and broadcast equipment',
    primaryConnectors: ['BNC', 'HDMI'],
    commonUses: ['Broadcast', 'Installation'],
    recommendedConnectors: ['BNCM', 'HDMIA']
  }
};

// Use case definitions with recommended connector types
export const useCases = {
  liveSound: {
    name: 'Live Sound',
    description: 'Professional audio for concerts, events, and live performances',
    primaryPurposes: ['microphone', 'instrument', 'speaker', 'power'],
    qualityLevel: 'premium',
    commonConnectors: {
      microphone: ['NC3FXX', 'NC3MXX', 'XLR3F', 'XLR3M'],
      instrument: ['TRS14M', 'TRS14F', 'TS14M', 'TS14F'],
      speaker: ['SPK4M', 'SPK4F', 'SPK2M', 'SPK2F'],
      power: ['PCB', 'PCG']
    }
  },
  studio: {
    name: 'Studio Recording',
    description: 'High-quality cables for recording studios and control rooms',
    primaryPurposes: ['microphone', 'instrument'],
    qualityLevel: 'elite',
    commonConnectors: {
      microphone: ['NC3FXX', 'NC3MXX', 'XLR3F', 'XLR3M'],
      instrument: ['TRS14M', 'TRS14F', 'TS14M', 'TS14F'],
      speaker: ['SPK4M', 'SPK4F']
    }
  },
  installation: {
    name: 'Installation',
    description: 'Permanent installation cables for venues and fixed systems',
    primaryPurposes: ['speaker', 'power', 'dmx'],
    qualityLevel: 'standard',
    commonConnectors: {
      speaker: ['SPK4M', 'SPK4F', 'SPK2M', 'SPK2F'],
      power: ['PCB', 'PCG', 'C13', 'C14'],
      dmx: ['XLR3M-DMX', 'XLR3F-DMX', 'XLR5M-DMX', 'XLR5F-DMX']
    }
  },
  broadcast: {
    name: 'Broadcast',
    description: 'Broadcast-grade cables for television, radio, and streaming',
    primaryPurposes: ['microphone', 'video', 'dmx'],
    qualityLevel: 'elite',
    commonConnectors: {
      microphone: ['NC3FXX', 'NC3MXX', 'XLR3F', 'XLR3M'],
      video: ['BNCM', 'HDMIA'],
      dmx: ['XLR5M-DMX', 'XLR5F-DMX']
    }
  },
  touring: {
    name: 'Touring',
    description: 'Rugged cables for touring musicians and production companies',
    primaryPurposes: ['microphone', 'instrument', 'speaker', 'power'],
    qualityLevel: 'premium',
    commonConnectors: {
      microphone: ['NC3FXX', 'NC3MXX', 'XLR3F', 'XLR3M'],
      instrument: ['TRS14M', 'TRS14F', 'TS14M', 'TS14F'],
      speaker: ['SPK4M', 'SPK4F', 'SPK2M', 'SPK2F'],
      power: ['PCB', 'PCG']
    }
  }
};

// Enhanced connector descriptions with clear naming
export const enhancedConnectorDescriptions = {
  // XLR Audio Connectors
  'NC3FXX': 'XLR 3-Pin Female (Neutrik) - Professional microphone/audio connector',
  'NC3MXX': 'XLR 3-Pin Male (Neutrik) - Professional microphone/audio connector',
  'XLR3F': 'XLR 3-Pin Female - Standard audio connector for microphones and line signals',
  'XLR3M': 'XLR 3-Pin Male - Standard audio connector for microphones and line signals',
  'XLR5F-DMX': 'XLR 5-Pin Female - Professional DMX lighting control connector',
  'XLR5M-DMX': 'XLR 5-Pin Male - Professional DMX lighting control connector',
  
  // TRS/TS Connectors
  'TRS14M': '1/4" TRS Male - Balanced audio connector for instruments and line signals',
  'TRS14F': '1/4" TRS Female - Balanced audio connector for instruments and line signals',
  'TS14M': '1/4" TS Male - Unbalanced audio connector for instruments and speakers',
  'TS14F': '1/4" TS Female - Unbalanced audio connector for instruments and speakers',
  'TRS35M': '3.5mm TRS Male - Stereo audio connector for consumer devices',
  'TRS35F': '3.5mm TRS Female - Stereo audio connector for consumer devices',
  'TS35M': '3.5mm TS Male - Mono audio connector for consumer devices',
  'TS35F': '3.5mm TS Female - Mono audio connector for consumer devices',
  
  // Speakon Connectors
  'SPK4M': 'Speakon 4-Pole Male - Professional speaker connector with locking mechanism',
  'SPK4F': 'Speakon 4-Pole Female - Professional speaker connector with locking mechanism',
  'SPK2M': 'Speakon 2-Pole Male - Compact speaker connector with locking mechanism',
  'SPK2F': 'Speakon 2-Pole Female - Compact speaker connector with locking mechanism',
  
  // Power Connectors
  'PCB': 'PowerCon Blue Female - Line output power connector (cable mount)',
  'PCBP': 'PowerCon Blue Male - Line input power connector (panel mount)',
  'PCG': 'PowerCon Grey Female - Line input power connector (cable mount)',
  'PCGP': 'PowerCon Grey Male - Line output power connector (panel mount)',
  'C13': 'IEC C13 Female - Standard equipment power inlet (cable mount)',
  'C14': 'IEC C14 Male - Standard equipment power outlet (cable mount)',
  'C13P': 'IEC C13 Female - Standard equipment power inlet (panel mount)',
  'C14P': 'IEC C14 Male - Standard equipment power outlet (panel mount)',
  '515P': 'NEMA 5-15P Male - Standard wall plug (cable mount)',
  '515R': 'NEMA 5-15R Female - Standard wall outlet (cable mount)',
  '515PP': 'NEMA 5-15P Male - Standard wall plug (panel mount)',
  '515RP': 'NEMA 5-15R Female - Standard wall outlet (panel mount)',
  'T1M': 'TRUE1 Male - Line input power connector (cable mount)',
  'T1F': 'TRUE1 Female - Line output power connector (cable mount)',
  'T1MP': 'TRUE1 Male - Line input power connector (panel mount)',
  'T1FP': 'TRUE1 Female - Line output power connector (panel mount)',
  
  // DMX Connectors
  'XLR3M-DMX': 'XLR 3-Pin Male (DMX) - Lighting control connector',
  'XLR3F-DMX': 'XLR 3-Pin Female (DMX) - Lighting control connector',
  
  // RCA Connectors
  'RCAM': 'RCA Male - Consumer audio/video connector',
  'RCAF': 'RCA Female - Consumer audio/video connector',
  // RCA Right Angle variants
  'RCAF-RA': 'RCA Female Right Angle - Consumer audio/video connector',
  'RCAM-RA': 'RCA Male Right Angle - Consumer audio/video connector',
  // RCA Panel Mount variants
  'RCAF-PM': 'RCA Female Panel Mount - Consumer audio/video connector',
  'RCAM-PM': 'RCA Male Panel Mount - Consumer audio/video connector'
};

// Function to get connectors by purpose and use case
export const getConnectorsByPurposeAndUseCase = (purpose, useCase = null) => {
  const purposeConfig = connectorPurposes[purpose];
  if (!purposeConfig) return [];
  
  let relevantConnectors = [];
  
  if (useCase && useCases[useCase]) {
    const useCaseConfig = useCases[useCase];
    relevantConnectors = useCaseConfig.commonConnectors[purpose] || [];
  } else {
    relevantConnectors = purposeConfig.recommendedConnectors || [];
  }
  
  // Get full connector objects with enhanced descriptions
  return relevantConnectors.map(connectorId => {
    // Try to find in each connector array
    let connector = connectorList.find(c => c.id === connectorId) ||
                   audioConnectors.find(c => c.id === connectorId) ||
                   powerConnectors.find(c => c.id === connectorId) ||
                   dmxConnectors.find(c => c.id === connectorId);
    
    if (connector) {
      return {
        ...connector,
        enhancedDescription: enhancedConnectorDescriptions[connectorId] || connector.description || connector.label,
        purpose: purpose,
        useCase: useCase
      };
    }
    return null;
  }).filter(Boolean);
};

// Function to get all connectors with enhanced descriptions
export const getAllConnectorsWithEnhancedDescriptions = () => {
  const allConnectors = [
    ...connectorList,
    ...audioConnectors,
    ...powerConnectors,
    ...dmxConnectors
  ];
  
  return allConnectors.map(connector => ({
    ...connector,
    enhancedDescription: enhancedConnectorDescriptions[connector.id] || connector.description || connector.label || connector.partNumber
  }));
};

// Function to categorize connectors by family for better organization
export const categorizeConnectorsByFamily = (connectors) => {
  const categories = {};
  
  connectors.forEach(connector => {
    const family = connector.family || connector.type || 'Other';
    if (!categories[family]) {
      categories[family] = [];
    }
    categories[family].push(connector);
  });
  
  return categories;
}; 

// Curated cable types for dropdowns and filtering
export const cableTypes = [
  { id: 'audio', label: 'Audio' },
  { id: 'power', label: 'Power' },
  { id: 'dmx', label: 'DMX' },
  { id: 'hybrid', label: 'Hybrid' },
  { id: 'video', label: 'Video' },
  // Add more as needed
]; 